import React from 'react';
import PropTypes from 'prop-types';
import { VaAdditionalInfo } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { recordEventOnce } from 'platform/monitoring/record-event';
import { ANALYTICS_EVENTS, HELP_TEXT_CLICKED_EVENT } from '../constants';
import { capitalizeEachWord, sippableId } from '../utils';
import { formatDate } from '../../shared/utils/dates';

export const limitedConsentTitle =
  'I want to limit my consent for VA to retrieve only specific information from my private medical providers.';

export const limitedConsentTextTitle =
  'Describe what you want to limit (treatment dates, condition type, etc.).';
export const treatmentLocation0781RelatedTitle =
  'Did you receive treatment at this facility related to the impact of any of your traumatic events?';
const { openedLimitedConsentHelp } = ANALYTICS_EVENTS;
export const limitedConsentDescription = (
  <VaAdditionalInfo
    trigger="What does this mean?"
    disableAnalytics
    onClick={() =>
      recordEventOnce(openedLimitedConsentHelp, HELP_TEXT_CLICKED_EVENT)
    }
  >
    <p>
      If you choose to limit consent, your doctor will follow the limitation you
      specify. Limiting consent could add to the time it takes to get your
      private medical records.
    </p>
  </VaAdditionalInfo>
);

export const recordReleaseDescription = () => (
  <div>
    <p>
      Tell us where you were treated for your condition. We’ll use the
      information you provide to get the records needed to support your claim.
    </p>
  </div>
);

export const ConfirmationPrivateMedicalRecordsRelease = ({ formData }) => {
  const facilities = formData?.providerFacility || [];
  const limitedConsent = formData?.limitedConsent;
  return (
    <>
      {limitedConsent && (
        <li>
          <h4>
            Option to limit consent to retrieve information from treatment
            providers
          </h4>
          <div className="vads-u-color--gray">{limitedConsentTextTitle}</div>
          <div>{limitedConsent}</div>
        </li>
      )}
      <li>
        <h4>Private medical provider details</h4>
        <div className="vads-u-padding--0">
          {facilities.map((facility, index) => {
            const facilityName = facility?.providerFacilityName;
            const address = facility?.providerFacilityAddress || {};
            const dateRange = facility?.treatmentDateRange || {};
            const treatedDisabilities = facility?.treatedDisabilityNames || {};
            const claimedKeys = Object.keys(treatedDisabilities).filter(
              key => key !== 'none' && treatedDisabilities[key],
            );
            const treatmentLocation0781Related =
              facility?.treatmentLocation0781Related;
            const newDisabilities = formData?.newDisabilities || [];
            const ratedDisabilities = formData?.ratedDisabilities || [];
            const newConditionNames = newDisabilities
              .filter(condition =>
                claimedKeys.includes(sippableId(condition.condition)),
              )
              .map(condition => capitalizeEachWord(condition.condition));
            const ratedConditionNames = ratedDisabilities
              .filter(disability =>
                claimedKeys.includes(sippableId(disability.name)),
              )
              .map(disability => capitalizeEachWord(disability.name));
            const treatedConditionNames = [
              ...newConditionNames,
              ...ratedConditionNames,
            ];

            return (
              <div
                key={index}
                className={index > 0 ? 'vads-u-margin-top--4' : ''}
              >
                <div className="vads-u-color--gray">
                  Name of private provider or hospital
                </div>
                <div>{facilityName}</div>
                {treatedConditionNames.length > 0 && (
                  <>
                    <div className="vads-u-color--gray vads-u-margin-top--1">
                      What conditions were you treated for?
                    </div>
                    <div className="vads-u-margin-top--0">
                      {treatedConditionNames.map(conditionName => (
                        <p key={conditionName} className="vads-u-margin-top--0">
                          {conditionName}
                        </p>
                      ))}
                    </div>
                  </>
                )}
                {treatmentLocation0781Related !== undefined && (
                  <>
                    <div className="vads-u-color--gray vads-u-margin-top--1">
                      {treatmentLocation0781RelatedTitle}
                    </div>
                    <div>{treatmentLocation0781Related ? 'Yes' : 'No'}</div>
                  </>
                )}
                {dateRange.from && (
                  <>
                    <div className="vads-u-color--gray vads-u-margin-top--1">
                      Treatment start date (You can provide an estimated date)
                    </div>
                    <div>{formatDate(dateRange.from)}</div>
                  </>
                )}
                {dateRange.to && (
                  <>
                    <div className="vads-u-color--gray vads-u-margin-top--1">
                      Treatment end date (You can provide an estimated date)
                    </div>
                    <div>{formatDate(dateRange.to)}</div>
                  </>
                )}
                {address.country && (
                  <>
                    <div className="vads-u-color--gray vads-u-margin-top--1">
                      Country
                    </div>
                    <div>{address.country}</div>
                  </>
                )}
                {address.street && (
                  <>
                    <div className="vads-u-color--gray vads-u-margin-top--1">
                      Street address
                    </div>
                    <div>{address.street}</div>
                  </>
                )}
                {address.street2 && (
                  <>
                    <div className="vads-u-color--gray vads-u-margin-top--1">
                      Street address line 2
                    </div>
                    <div>{address.street2}</div>
                  </>
                )}
                {address.city && (
                  <>
                    <div className="vads-u-color--gray vads-u-margin-top--1">
                      City
                    </div>
                    <div>{address.city}</div>
                  </>
                )}
                {address.state && (
                  <>
                    <div className="vads-u-color--gray vads-u-margin-top--1">
                      State
                    </div>
                    <div>{address.state}</div>
                  </>
                )}
                {address.postalCode && (
                  <>
                    <div className="vads-u-color--gray vads-u-margin-top--1">
                      Postal code
                    </div>
                    <div>{address.postalCode}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </li>
    </>
  );
};

ConfirmationPrivateMedicalRecordsRelease.propTypes = {
  formData: PropTypes.shape({
    limitedConsent: PropTypes.string,
    newDisabilities: PropTypes.arrayOf(
      PropTypes.shape({
        condition: PropTypes.string,
      }),
    ),
    ratedDisabilities: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      }),
    ),
    providerFacility: PropTypes.arrayOf(
      PropTypes.shape({
        providerFacilityName: PropTypes.string,
        treatedDisabilityNames: PropTypes.object,
        treatmentLocation0781Related: PropTypes.bool,
        providerFacilityAddress: PropTypes.shape({
          country: PropTypes.string,
          street: PropTypes.string,
          street2: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
          postalCode: PropTypes.string,
        }),
        treatmentDateRange: PropTypes.shape({
          from: PropTypes.string,
          to: PropTypes.string,
        }),
      }),
    ),
  }),
};
